/**
 * The questionnaire as the person will meet it — **usable**, and thrown away when it closes.
 *
 * It is the real `GLModal` from `gl-ui`, centred, the same component the dashboard renders, so the
 * width, the padding and what happens to a question that is too long are the real ones. A
 * thumbnail in a side column would agree with the dashboard everywhere except in the case worth
 * checking.
 *
 * **Every control answers.** A preview that can only be read hides exactly what the eye does not
 * catch: that the required question does not release "Continuar", that the exclusive option does
 * not clear the others, that step 3 has six questions and scrolls too far, that the selected label
 * wraps badly. Walking through it is how those turn up — and here they turn up for the person
 * building it, who can still fix them.
 *
 * "Continuar" runs the **real** step gate (R16b, in `previewAnswers.ts`): required questions of
 * this step answered, selections within `minSelections`/`maxSelections`, text within `maxLength`.
 * That rule lives in the client, so this is the only place it can be caught being wrong before a
 * real person meets it.
 *
 * **Nothing leaves the screen.** No request, no saved response; the answers live in this
 * component's state and die with it. Closing discards them, and reopening starts clean — a preview
 * that remembered what was typed would be a ghost draft, confusing on the second look. The last
 * step shows the submit button, inert, so the end of the questionnaire is visible.
 *
 * **The controls are `gl-ui`'s**, not hand-drawn ones: `UTFormControl` for every choice — the radio
 * circles of a scale included, the way `GLOptInOptOutModal` already asks for a reason —
 * `UTTextField` for a short answer and `UTTextArea` for a long one. A scale drawn as square buttons
 * here and as radio circles in the dashboard would send whoever is judging the content to argue
 * with the control instead.
 *
 * **What is still an approximation:** the arrangement of those controls. The renderer the dashboard
 * will use is stage 06 of this feature and does not exist yet; when it does, these bodies should be
 * replaced by its components. Everything around them — the modal, its size, the step flow, the
 * validation, the order, the labels — is already the real thing.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTFormControl, UTModal, UTTextArea, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { ChangeEvent, JSX } from 'react';
import type { SurveyLanguage } from '../../../Types/Surveys.types';
import type { PreviewAnswer, PreviewAnswers } from '../../../Utils/previewAnswers';
import {
  isAtSelectionCap,
  rowAnswerKey,
  stepProblems,
  toggleOption,
} from '../../../Utils/previewAnswers';
import type { SurveyFormData, SurveyQuestionDraft } from '../../../Utils/surveyForm';
import { questionsOfStep, stepsOf } from '../../../Utils/surveyForm';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  padding-bottom: var(--space-s);
`;

/** A strip, not a paragraph in the flow: between the last question and the buttons it read as part
    of the questionnaire. */
const bannerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  margin: calc(var(--space-s) * -1) calc(var(--space-ml) * -1) 0;
  padding: var(--space-xs) var(--space-ml);
  background: var(--color-surface-sunken);
  border-bottom: 1px solid var(--color-divider);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const questionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
`;

const promptStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-wrap: pretty;
`;

const helpStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const missingStyle = css`
  font-style: italic;
  color: var(--color-danger);
`;

const requiredStyle = css`
  color: var(--color-danger);
`;

const choiceListStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

/** Only the cap's dimming lives here. Everything the row draws — the circle, the box, the label —
    is `UTFormControl`'s. */
const choiceRowStyle = css`
  &[data-disabled='true'] {
    opacity: 0.45;
  }
`;

/**
 * A label nobody filled in is still shown, and shown as unfilled — the preview is where that is
 * supposed to be noticed. `GLTypography` writes its colour as an inline style, so this is the one
 * place the sheet has to shout over it.
 */
const missingChoiceStyle = css`
  & .gl-checkbox__container > span {
    font-style: italic;
    color: var(--color-danger) !important;
  }
`;

/**
 * The scale takes the whole width instead of huddling on the left: the options are spread out, and
 * the two end labels below land under the first and the last circle, which is what makes
 * "Muito difícil" and "Muito fácil" read as the ends of *this* scale.
 */
const scaleStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-xs);
  width: 100%;

  /* The number belongs under its circle, not beside it: eleven pairs side by side do not fit the
     modal, and the row stops reading as a scale. */
  .gl-checkbox__container {
    flex-direction: column;
    gap: 4px;
  }
`;

const endsStyle = css`
  display: flex;
  justify-content: space-between;
  gap: var(--space-s);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const counterStyle = css`
  align-self: flex-end;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const matrixWrapStyle = css`
  overflow-x: auto;
`;

const matrixStyle = css`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-xs);
`;

const matrixHeadStyle = css`
  padding: 6px 8px;
  text-align: center;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
`;

const matrixRowHeadStyle = css`
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid var(--color-divider);
`;

const matrixCellStyle = css`
  padding: 6px 8px;
  text-align: center;
  border-bottom: 1px solid var(--color-divider);

  /* The control is a flex label; without this it hugs the left edge of the cell. */
  .gl-checkbox__container {
    justify-content: center;
  }
`;

const stepLineStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

/** What is holding the step back, in the place the person is about to press. */
const gateStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-s) var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  font-size: var(--font-size-2xs);
  color: var(--gl-yellow-dark);
`;

const endNoteStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const Text = ({ value, fallback }: { value: string; fallback: string }): JSX.Element =>
  value.trim() !== '' ? <span>{value}</span> : <span className={missingStyle}>{fallback}</span>;

const orMissing = (value: string, fallback: string): string =>
  value.trim() === '' ? fallback : value;

interface QuestionBodyProps {
  question: SurveyQuestionDraft;
  language: SurveyLanguage;
  answers: PreviewAnswers;
  onAnswer: (key: string, answer: PreviewAnswer | undefined) => void;
}

const QuestionBody = ({
  question,
  language,
  answers,
  onAnswer,
}: QuestionBodyProps): JSX.Element => {
  const texts = question.configTexts[language];
  const options = question.options.filter(option => option.value.trim() !== '');
  const answer = answers[question.uid];
  const selected = answer?.kind === 'options' ? answer.value : [];

  if (question.type === 'SINGLE_CHOICE' || question.type === 'MULTI_CHOICE') {
    if (options.length === 0) {
      return <span className={missingStyle}>nenhuma opção cadastrada</span>;
    }

    const atCap = question.type === 'MULTI_CHOICE' && isAtSelectionCap(question, selected);

    return (
      <div className={choiceListStyle}>
        {options.map(option => {
          const isSelected = selected.includes(option.uid);
          /* The cap never blocks "nenhuma delas": it is the way out of a list, and someone who
             already picked three has to be able to say none of them after all. */
          const isBlocked = atCap && !isSelected && !option.isExclusive;

          const label = option.labels[language];

          return (
            <div
              key={option.uid}
              className={
                label.trim() === '' ? `${choiceRowStyle} ${missingChoiceStyle}` : choiceRowStyle
              }
              data-disabled={isBlocked}
            >
              <UTFormControl
                id={`survey-preview-option-${question.uid}-${option.value}`}
                name={`survey-preview-${question.uid}`}
                value={option.value}
                variant='primary'
                type={question.type === 'MULTI_CHOICE' ? 'checkbox' : 'radio'}
                text={orMissing(label, `sem rótulo (${option.value})`)}
                checked={isSelected}
                disabled={isBlocked}
                onChange={() => {
                  onAnswer(question.uid, {
                    kind: 'options',
                    value: toggleOption(question, selected, option.uid),
                  });
                }}
              />
            </div>
          );
        })}
        {question.type === 'MULTI_CHOICE' && question.numbers.maxSelections !== undefined ? (
          <span className={counterStyle}>
            {selected.length} de {question.numbers.maxSelections}
          </span>
        ) : null}
      </div>
    );
  }

  if (question.type === 'RATING' || question.type === 'NPS') {
    const min = question.type === 'NPS' ? 0 : (question.numbers.min ?? 1);
    const max = question.type === 'NPS' ? 10 : (question.numbers.max ?? 5);
    const steps = max >= min ? Array.from({ length: max - min + 1 }, (_i, n) => min + n) : [];
    const picked = answer?.kind === 'number' ? answer.value : undefined;
    const leftLabel = question.type === 'NPS' ? texts.leftLabel : texts.minLabel;
    const rightLabel = question.type === 'NPS' ? texts.rightLabel : texts.maxLabel;

    return (
      <>
        <div
          className={scaleStyle}
          role='radiogroup'
          aria-label={orMissing(question.labels[language], question.key)}
        >
          {steps.map(value => (
            <UTFormControl
              key={value}
              id={`survey-preview-scale-${question.uid}-${value}`}
              name={`survey-preview-${question.uid}`}
              value={String(value)}
              variant='primary'
              type='radio'
              text={String(value)}
              checked={picked === value}
              onChange={() => {
                onAnswer(question.uid, { kind: 'number', value });
              }}
            />
          ))}
        </div>
        <div className={endsStyle}>
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      </>
    );
  }

  if (question.type === 'BOOLEAN') {
    const picked = answer?.kind === 'boolean' ? answer.value : undefined;

    return (
      <div className={choiceListStyle}>
        {[true, false].map(value => {
          const label = value ? texts.trueLabel : texts.falseLabel;

          return (
            <div
              key={String(value)}
              className={
                label.trim() === '' ? `${choiceRowStyle} ${missingChoiceStyle}` : choiceRowStyle
              }
            >
              <UTFormControl
                id={`survey-preview-bool-${question.uid}-${String(value)}`}
                name={`survey-preview-${question.uid}`}
                value={String(value)}
                variant='primary'
                type='radio'
                text={orMissing(label, value ? 'sem rótulo para "sim"' : 'sem rótulo para "não"')}
                checked={picked === value}
                onChange={() => {
                  onAnswer(question.uid, { kind: 'boolean', value });
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  if (question.type === 'MATRIX') {
    if (options.length === 0 || question.rows.length === 0) {
      return <span className={missingStyle}>a grade precisa de uma escala e de linhas</span>;
    }

    return (
      <div className={matrixWrapStyle}>
        <table className={matrixStyle}>
          <thead>
            <tr>
              <th className={matrixHeadStyle} />
              {options.map(option => (
                <th key={option.uid} className={matrixHeadStyle}>
                  {orMissing(option.labels[language], option.value)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {question.rows.map(row => {
              const key = rowAnswerKey(question.uid, row.uid);
              const rowAnswer = answers[key];
              const rowSelected = rowAnswer?.kind === 'options' ? rowAnswer.value : [];

              return (
                <tr key={row.uid}>
                  <th className={matrixRowHeadStyle} scope='row'>
                    {orMissing(row.labels[language], row.key || 'sem rótulo')}
                    {row.isRequired ? <span className={requiredStyle}> *</span> : null}
                  </th>
                  {options.map(option => (
                    <td key={option.uid} className={matrixCellStyle}>
                      <UTFormControl
                        id={`survey-preview-matrix-${key}-${option.value}`}
                        name={`survey-preview-${key}`}
                        value={option.value}
                        variant='primary'
                        type='radio'
                        aria-label={`${orMissing(row.labels[language], row.key)} · ${orMissing(
                          option.labels[language],
                          option.value,
                        )}`}
                        checked={rowSelected.includes(option.uid)}
                        onChange={() => {
                          onAnswer(key, {
                            kind: 'options',
                            value: [option.uid],
                          });
                        }}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  const text = answer?.kind === 'text' ? answer.value : '';
  const maxLength = question.numbers.maxLength;
  const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    onAnswer(question.uid, { kind: 'text', value: event.target.value });
  };

  /* `UTTextArea` counts the characters itself; `UTTextField` has no counter, so the short answer
     keeps the one written here. */
  if (question.type === 'LONG_TEXT') {
    return (
      <UTTextArea
        id={`survey-preview-text-${question.uid}`}
        name={`survey-preview-${question.uid}`}
        textAreaSize='medium'
        placeholder={texts.placeholder}
        value={text}
        aria-label={orMissing(question.labels[language], question.key)}
        {...(maxLength !== undefined && {
          characterLimit: maxLength,
          maxLength,
        })}
        onChange={onChange}
      />
    );
  }

  return (
    <>
      <UTTextField
        id={`survey-preview-text-${question.uid}`}
        name={`survey-preview-${question.uid}`}
        type='text'
        placeholder={texts.placeholder}
        maxLength={maxLength}
        value={text}
        aria-label={orMissing(question.labels[language], question.key)}
        onChange={onChange}
      />
      {maxLength !== undefined ? (
        <span className={counterStyle}>
          {text.length} / {maxLength}
        </span>
      ) : null}
    </>
  );
};

export interface SurveyPreviewModalProps {
  form: SurveyFormData;
  language: SurveyLanguage;
  onClose: () => void;
}

export const SurveyPreviewModal = ({
  form,
  language,
  onClose,
}: SurveyPreviewModalProps): JSX.Element => {
  const steps = stepsOf(form.questions);
  const [stepIndex, setStepIndex] = useState(0);
  /** Lives here and nowhere else: the modal unmounts on close, and the answers go with it. */
  const [answers, setAnswers] = useState<PreviewAnswers>({});

  const isSinglePage = form.layout === 'SINGLE_PAGE';
  const currentStep = steps[Math.min(stepIndex, steps.length - 1)];
  const shown: SurveyQuestionDraft[] = isSinglePage
    ? [...form.questions].sort((a, b) => a.step - b.step)
    : questionsOfStep(form.questions, currentStep);

  const isLast = isSinglePage || stepIndex >= steps.length - 1;

  const problems = useMemo(
    () => stepProblems(shown, answers, language),
    [answers, language, shown],
  );

  const setAnswer = (key: string, answer: PreviewAnswer | undefined): void => {
    setAnswers(current => ({ ...current, [key]: answer }));
  };

  return (
    <UTModal
      open
      position='center'
      maxWidth='560px'
      hasButtonClose
      modalContentId='survey-builder-preview-modal'
      closeButtonId='survey-builder-preview-close'
      title={orMissing(form.texts[language].title, '(sem título)')}
      description={form.texts[language].description || undefined}
      content={
        <div className={contentStyle}>
          <span id='survey-preview-banner' className={bannerStyle}>
            Preview: responda à vontade, nada é salvo.
          </span>

          {isSinglePage ? null : (
            <span className={stepLineStyle}>
              Passo {stepIndex + 1} de {steps.length}
            </span>
          )}

          {shown.length === 0 ? (
            <span className={missingStyle}>Este passo está sem perguntas.</span>
          ) : (
            shown.map(question => (
              <div key={question.uid} className={questionStyle}>
                <span className={promptStyle}>
                  <Text value={question.labels[language]} fallback='(sem enunciado)' />
                  {question.isRequired ? <span className={requiredStyle}> *</span> : null}
                </span>
                {question.helpTexts[language].trim() !== '' ? (
                  <span className={helpStyle}>{question.helpTexts[language]}</span>
                ) : null}
                <QuestionBody
                  question={question}
                  language={language}
                  answers={answers}
                  onAnswer={setAnswer}
                />
              </div>
            ))
          )}

          {problems.length > 0 ? (
            <div id='survey-preview-gate' className={gateStyle}>
              {problems.map(problem => (
                <span key={problem}>{problem}</span>
              ))}
            </div>
          ) : null}

          {isLast ? (
            <span id='survey-preview-end-note' className={endNoteStyle}>
              Fim do questionário. No preview o envio não acontece — feche quando quiser, nada fica
              guardado.
            </span>
          ) : null}
        </div>
      }
      customButtons={
        isSinglePage
          ? [
              {
                text: 'Enviar',
                id: 'survey-preview-submit',
                disabled: true,
                onClick: () => undefined,
              },
            ]
          : [
              {
                text: 'Voltar',
                variant: 'secondary',
                id: 'survey-preview-back',
                disabled: stepIndex === 0,
                onClick: () => {
                  setStepIndex(current => Math.max(0, current - 1));
                },
              },
              {
                text: isLast ? 'Enviar' : 'Continuar',
                id: isLast ? 'survey-preview-submit' : 'survey-preview-next',
                // The real gate: the last step's button stays inert on purpose, so the end of the
                // questionnaire is visible without anything being sent.
                disabled: isLast || problems.length > 0,
                onClick: () => {
                  if (isLast) return;

                  setStepIndex(current => Math.min(steps.length - 1, current + 1));
                },
              },
            ]
      }
      onClose={onClose}
    />
  );
};
